// Pages/inventario/components/UbicacionesProductoPopover.tsx
//
// «En N lugares» — round 2026-08-06.
//
// Cada fila de la tabla es un producto EN UN LUGAR, así que la pregunta más
// natural del inventario —«¿cuánto tengo de esto en total?»— no tenía respuesta
// en pantalla: había que buscar el nombre a ojo y confiar en que ninguna
// variante de escritura lo escondiera.
//
// El tag aparece solo cuando el producto vive en más de un lugar. Mostrarlo
// siempre («en 1 lugar») sería ruido en la mayoría de las filas y le quitaría
// justamente el valor de señal que tiene.
import React, { useState } from "react";
import { Popover, Spin, Table, Tag, Typography } from "antd";
import { inventarioApi } from "../../../services/inventario.api";
import { UbicacionTag } from "./ProductoCells";
import "../inventario.css";

const { Text } = Typography;

interface Fila {
  id: number;
  esta: boolean;
  ubicacionTipo: string | null;
  ubicacionNombre: string | null;
  cantidadDeUnidades: number;
  cantidadDePacks: number;
  esPack: boolean;
  oculto: boolean;
}

interface Datos {
  nombre: string;
  ubicaciones: Fila[];
  totalUnidades: number;
  totalPacks: number;
}

const UbicacionesProductoPopover: React.FC<{
  productoId: number;
  otras: number;
}> = ({ productoId, otras }) => {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [cargando, setCargando] = useState(false);

  // Se pide al ABRIR, no al renderizar la tabla: con 50 filas serían 50
  // consultas para un dato que casi siempre nadie mira.
  //
  // Y se vuelve a pedir en CADA apertura. La primera versión cacheaba con
  // `if (datos) return`, así que después de mover stock o registrar una compra
  // el popover seguía mostrando el reparto viejo mientras la fila de al lado ya
  // decía otra cosa. Un dato de stock desactualizado que se ve actual es peor
  // que no tenerlo. Se conserva lo anterior en pantalla mientras llega lo nuevo
  // para no parpadear.
  const cargar = async (abierto: boolean) => {
    if (!abierto || cargando) return;
    setCargando(true);
    const res = await inventarioApi.getUbicaciones(productoId);
    if (res?.data) setDatos(res.data);
    setCargando(false);
  };

  // El orden importa: si ya hay datos se muestran aunque esté recargando, para
  // que reabrir el popover no lo deje en blanco por un instante.
  const contenido = datos ? (
    <div className="inventario-popover-ubicaciones">
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={datos.ubicaciones}
        columns={[
          {
            title: "Lugar",
            key: "lugar",
            render: (_: unknown, f: Fila) => (
              <span>
                <UbicacionTag tipo={f.ubicacionTipo} nombre={f.ubicacionNombre} />
                {/* Marcar dónde estás parado: sin esto hay que comparar los
                    códigos a mano para ubicarse en la propia lista. */}
                {f.esta ? <Tag color="default">esta fila</Tag> : null}
                {f.oculto ? <Tag color="orange">de baja</Tag> : null}
              </span>
            ),
          },
          {
            title: "Stock",
            key: "stock",
            align: "right",
            render: (_: unknown, f: Fila) =>
              f.esPack
                ? `${f.cantidadDePacks} pack${f.cantidadDePacks === 1 ? "" : "s"} (${f.cantidadDeUnidades} u.)`
                : `${f.cantidadDeUnidades} u.`,
          },
        ]}
      />
      <div className="inventario-popover-total">
        <Text strong>Total: {datos.totalUnidades} unidades</Text>
        {datos.totalPacks > 0 && (
          <Text type="secondary"> · {datos.totalPacks} packs</Text>
        )}
      </div>
    </div>
  ) : cargando ? (
    <div className="inventario-popover-cargando">
      <Spin size="small" />
    </div>
  ) : (
    <Text type="secondary">No se pudo cargar.</Text>
  );

  return (
    <Popover
      trigger="click"
      onOpenChange={cargar}
      title="El mismo producto, repartido"
      content={contenido}
    >
      <Tag color="blue" className="inventario-tag-mini inventario-tag-clickable">
        en {otras + 1} lugares
      </Tag>
    </Popover>
  );
};

export default UbicacionesProductoPopover;
